import { useEffect, useState } from "react";
import axios from "axios";
import { FileText, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { CohortSelector } from "./CohortSelector";
import { upcomingCohorts } from "../lib/cohorts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { company_name: "", contact_name: "", email: "", phone: "", address: "", notes: "" };

const Field = ({ id, label, children, error }) => (
  <div className="space-y-1">
    <Label htmlFor={id} className="text-xs">{label}</Label>
    {children}
    {error && <p className="text-xs text-destructive">{error}</p>}
  </div>
);

export const ProformaRequestDialog = ({ courseId, format, participants, cohortId, triggerClassName = "" }) => {
  const [open, setOpen] = useState(false);
  const [course, setCourse] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [bank, setBank] = useState("jamaica");
  const [cohort, setCohort] = useState(cohortId || null);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (open && courseId) axios.get(`${API}/courses/${courseId}`).then((r) => setCourse(r.data)).catch(() => setCourse(null));
  }, [open, courseId]);
  useEffect(() => setCohort(cohortId || null), [cohortId]);

  const cohorts = upcomingCohorts(course, format);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    const e = {};
    if (!form.company_name.trim()) e.company_name = "Required";
    if (!form.contact_name.trim()) e.contact_name = "Required";
    if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
    if (cohorts.length && !cohorts.some((c) => c.id === cohort)) e.cohort = "Please select course dates";
    setErrors(e);
    if (Object.keys(e).length) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/proforma/request`, {
        ...form, course_id: courseId, delivery_format: format, participants: Number(participants) || 1,
        cohort_id: cohorts.length ? cohort : null, bank_choice: bank,
      });
      setDone(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit request");
    } finally {
      setSending(false);
    }
  };

  const reset = (o) => { setOpen(o); if (!o) { setDone(null); setForm(EMPTY); setErrors({}); } };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" disabled={!courseId || !format} className={`border-brand text-brand hover:bg-brand/5 ${triggerClassName}`} data-testid="proforma-request-btn">
          <FileText className="mr-2 h-4 w-4" /> Request a Pro Forma Invoice
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="proforma-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Request a Pro Forma Invoice</DialogTitle>
          <DialogDescription>
            {course ? `${participants} × ${course.title}` : "Loading course…"} — our team will review and email your verifiable pro forma (valid 30 days).
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="text-center py-6 space-y-3" data-testid="proforma-success">
            <CheckCircle2 className="h-12 w-12 text-brand mx-auto" />
            <p className="font-heading font-semibold text-lg">Request {done.number} received</p>
            <p className="text-sm text-muted-foreground">Estimated total {`USD ${Number(done.total).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}. We'll email the pro forma to {form.email} once reviewed.</p>
            <Button onClick={() => reset(false)} className="bg-brand text-white" data-testid="proforma-close-btn">Close</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field id="pf-company" label="Company name *" error={errors.company_name}><Input id="pf-company" value={form.company_name} onChange={set("company_name")} data-testid="proforma-company-input" /></Field>
              <Field id="pf-contact" label="Contact person *" error={errors.contact_name}><Input id="pf-contact" value={form.contact_name} onChange={set("contact_name")} data-testid="proforma-contact-input" /></Field>
              <Field id="pf-email" label="Email *" error={errors.email}><Input id="pf-email" type="email" value={form.email} onChange={set("email")} data-testid="proforma-email-input" /></Field>
              <Field id="pf-phone" label="Phone"><Input id="pf-phone" value={form.phone} onChange={set("phone")} data-testid="proforma-phone-input" /></Field>
            </div>
            <Field id="pf-address" label="Billing address"><Input id="pf-address" value={form.address} onChange={set("address")} data-testid="proforma-address-input" /></Field>
            {cohorts.length > 0 && <CohortSelector cohorts={cohorts} value={cohort} onChange={setCohort} error={errors.cohort} />}
            <div className="space-y-2">
              <Label className="text-xs">Which bank account will you pay into? *</Label>
              <RadioGroup value={bank} onValueChange={setBank} className="grid sm:grid-cols-2 gap-2">
                {[["jamaica", "Jamaica (JMMB — USD or JMD)"], ["us", "United States (Wells Fargo — USD)"]].map(([v, l]) => (
                  <label key={v} htmlFor={`pf-bank-${v}`} className={`flex items-center gap-2 border rounded-lg p-3 cursor-pointer text-sm ${bank === v ? "border-brand bg-brand/5" : ""}`}>
                    <RadioGroupItem value={v} id={`pf-bank-${v}`} data-testid={`proforma-bank-${v}`} /> {l}
                  </label>
                ))}
              </RadioGroup>
            </div>
            <Field id="pf-notes" label="Notes (PO number, VAT/TRN, etc.)"><Textarea id="pf-notes" rows={2} value={form.notes} onChange={set("notes")} data-testid="proforma-notes-input" /></Field>
            <Button onClick={submit} disabled={sending} className="w-full bg-brand hover:bg-brand-dark text-white" data-testid="proforma-submit-btn">
              {sending ? "Sending…" : "Submit Request"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
